import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DashboardLogo } from "@/components/dashboard/DashboardLogo";

// User credentials database
const USER_CREDENTIALS = [
  {
    email: "itsm@cyntralabs.com",
    password: "password123",
    name: "ITSM Admin",
    role: "Administrator"
  },
  {
    email: "rishabh.yadav@cyntralabs.com",
    password: "rishabh2024",
    name: "Rishabh Yadav",
    role: "Service Manager"
  },
  {
    email: "chhavi@cyntralabs.com",
    password: "chhavi2024",
    name: "Chhavi",
    role: "Operations Analyst"
  },
  {
    email: "shravan@cyntralabs.com",
    password: "shravan2024",
    name: "Shravan",
    role: "Technical Lead"
  },
  {
    email: "aman.singh@cyntralabs.com",
    password: "aman2024",
    name: "Aman Singh",
    role: "Incident Manager"
  },
  {
    email: "shivam@cyntralabs.com",
    password: "shivam2024",
    name: "Shivam",
    role: "Service Analyst"
  },
  {
    email: "cponce@markanthony.com",
    password: "crispin2024",
    name: "Crispin Ponce",
    role: "End User"
  },
  {
    email: "ykostenko@markanthony.com",
    password: "yurii2024",
    name: "Yurii Kostenko",
    role: "End User"
  },
  {
    email: "julian.fernandez@markanthony.com",
    password: "julian2024",
    name: "Julian Fernandez",
    role: "End User"
  },
  {
    email: "sebastian.denker@markanthony.com",
    password: "sebastian2024",
    name: "Sebastian Denker",
    role: "End User"
  },
  {
    email: "ariel.montalvo@markanthony.com",
    password: "ariel2024",
    name: "Ariel Montalvo",
    role: "End User"
  },
  {
    email: "david.berber@markanthony.com",
    password: "david2024",
    name: "David Berber",
    role: "End User"
  },
  {
    email: "roswiljexander.asca@markanthony.com",
    password: "roswil2024",
    name: "Roswil Ascanio",
    role: "End User"
  },
  {
    email: "uriel.lara@markanthony.com",
    password: "uriel2024",
    name: "Uriel Lara",
    role: "End User"
  },
  {
    email: "alan.baez@markanthony.com",
    password: "alan2024",
    name: "Alan Baez",
    role: "End User"
  },
  {
    email: "josealejandro.urest@markanthony.com",
    password: "alejandro2024",
    name: "Alejandro Uresti",
    role: "End User"
  },
  {
    email: "Abhishek.basu@markanthony.com",
    password: "abhishek2024",
    name: "Abhishek Basu",
    role: "End User"
  },
  {
    email: "alfonso.perez@markanthony.com",
    password: "alfonso2024",
    name: "Alfonso Perez",
    role: "End User"
  },
  {
    email: "federico.dino@markanthony.com",
    password: "federico2024",
    name: "Federico Dino",
    role: "End User"
  },
  {
    email: "irving.echeverria@markanthony.com",
    password: "irving2024",
    name: "Irving Echeverría",
    role: "End User"
  },
  {
    email: "prasad.moyre@markanthony.com",
    password: "prasad2024",
    name: "Prasad Morye",
    role: "End User"
  },
  {
    email: "sebastian.petriw@markanthony.com",
    password: "sebastian2024",
    name: "Sebastian Petriw",
    role: "End User"
  },
  {
    email: "santiago.casabo@markanthony.com",
    password: "santiago2024",
    name: "Santiago Casabo",
    role: "End User"
  },
  {
    email: "federicoariel.gonza@markanthony.com",
    password: "federico2024",
    name: "Federico Gonzalez",
    role: "End User"
  },
  {
    email: "giulia.pugliese@markanthony.com",
    password: "giulia2024",
    name: "Giulia Pugliese",
    role: "End User"
  },
  {
    email: "matteo.carbone@markanthony.com",
    password: "matteo2024",
    name: "Matteo Carbone",
    role: "End User"
  },
  {
    email: "francesco.nistico2@markanthony.com",
    password: "francesco2024",
    name: "Francesco Nistico",
    role: "End User"
  }
];

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate authentication delay
    setTimeout(() => {
      // Find user in credentials database
      const user = USER_CREDENTIALS.find(
        (u) => u.email === email && u.password === password
      );

      if (user) {
        // Generate a unique token for this session
        const token = `itsm-token-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Set session data in localStorage
        localStorage.setItem("itsm-auth-token", token);
        localStorage.setItem("itsm-user", user.email);
        localStorage.setItem("itsm-user-name", user.name);
        localStorage.setItem("itsm-user-role", user.role);
        
        toast.success(`Welcome back, ${user.name}!`);
        navigate("/dashboard");
      } else {
        toast.error("Invalid email or password");
      }
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <DashboardLogo className="mb-4" />
          <CardTitle className="text-2xl font-bold text-center">ITSM Insights Dashboard</CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="your.email@example.com" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password" 
                type="password" 
                placeholder="••••••••" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
        
        <CardFooter className="justify-center">
          {/* <p className="text-xs text-center text-muted-foreground">
            Demo credentials: itsm@cyntralabs.com / password123
          </p> */}
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
